import React, { useMemo, useState } from "react";
import {
  FieldsetKeyMapping,
  createFieldsetKeyMapping,
  deleteFieldsetKeyMapping,
  updateFieldsetKeyMapping,
} from "../../../api/fieldsetApi";
import { useSelector } from "react-redux";
import { RootState, store } from "../../../utils/store";
import {
  Button,
  Icon,
  PageDimmer,
  SearchBar,
} from "../../../components/tailwind";
import {
  NotificationAppearance,
  setDjangoToastOpen,
} from "../../../api/djangoToastSlice";
import { BooleanFilterType } from "./Fieldsets";
import { KnownKey } from "./types";

const filterOnMappings = (
  keys: KnownKey[],
  filterMappings: BooleanFilterType,
  keyMappings: FieldsetKeyMapping
): KnownKey[] => {
  switch (filterMappings) {
    case "true":
      return keys.filter(({ key }) => !!keyMappings[key]);
    case "false":
      return keys.filter(({ key }) => !keyMappings[key]);
    default:
      return keys;
  }
};

const filterOnSearch = (keys: KnownKey[], search: string): KnownKey[] => {
  if (!search) return keys;
  return keys.filter(({ key }) =>
    key.toLowerCase().includes(search.toLowerCase())
  );
};

const CHECKBOX_CLASSES =
  "tw-form-checkbox tw-h-4 tw-w-4 tw-rounded tw-border-gray-400 tw-text-textual-blue focus:tw-ring-gray-400";

type Props = {
  keyMappings: FieldsetKeyMapping;
  knownKeys: KnownKey[];
  loading: boolean;
  reFetch: () => Promise<void>;
  search: string;
  setSearch: (search?: string) => void;
  filterMappings: BooleanFilterType;
  setFilterMappings: (filter?: BooleanFilterType) => void;
};

export const FieldsetMappingsForm: React.FC<Props> = ({
  keyMappings,
  knownKeys,
  loading,
  reFetch,
  search,
  setSearch,
  filterMappings,
  setFilterMappings,
}) => {
  const token = useSelector((state: RootState) => state.auth.token);
  const [editingKey, setEditingKey] = useState<string>();

  const handleUpdateKeyMapping = async (
    key: string,
    displayKey: string
  ): Promise<void> => {
    await updateFieldsetKeyMapping(token, key, displayKey).finally(() =>
      reFetch()
    );
    store.dispatch(
      setDjangoToastOpen({
        content: "Input Schema Key Mapping Updated",
        appearance: NotificationAppearance.SUCCESS,
        additionalContent: `Key: ${key} will now use ${displayKey}`,
      })
    );
    setEditingKey(undefined);
  };

  const handleCreateKeyMapping = async (
    key: string,
    displayKey: string
  ): Promise<void> => {
    await createFieldsetKeyMapping(token, key, displayKey).finally(() =>
      reFetch()
    );
    store.dispatch(
      setDjangoToastOpen({
        content: "Input Schema Key Mapping Created",
        appearance: NotificationAppearance.SUCCESS,
        additionalContent: `Key: ${key} will now use ${displayKey}`,
      })
    );
    setEditingKey(undefined);
  };

  const handleDeleteKeyMapping = async (key: string): Promise<void> => {
    await deleteFieldsetKeyMapping(token, key).finally(() => reFetch());
    store.dispatch(
      setDjangoToastOpen({
        content: "Input Schema Key Mapping Deleted",
        appearance: NotificationAppearance.WARNING,
        additionalContent: `Key: ${key} is now unmapped`,
      })
    );
    setEditingKey(undefined);
  };

  const filteredKnownKeys = useMemo(() => {
    return filterOnSearch(
      filterOnMappings(knownKeys, filterMappings, keyMappings),
      search
    );
  }, [filterMappings, search, knownKeys, keyMappings]);

  const resetFilters = (): void => {
    setSearch();
    setFilterMappings();
  };

  const handleEditKeyMapping = (key: string): void => {
    if (editingKey === key) {
      setEditingKey(undefined);
      return;
    }
    setEditingKey(key);
  };

  return (
    <div
      className="tw-flex tw-flex-col tw-gap-3 tw-py-2"
      data-testid="fieldset-mappings-form"
    >
      <SearchBar
        value={search || ""}
        onValueChange={(value): void => setSearch(value)}
        placeholder="Search fields"
        data-testid="fieldset-mappings-form-search-keys"
      />
      <div className="tw-flex tw-items-center tw-gap-4 tw-flex-wrap">
        <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-cursor-pointer">
          <input
            type="checkbox"
            data-testid="fieldset-mappings-form-show-all-mapped"
            checked={filterMappings === "true"}
            onChange={(): void => setFilterMappings("true")}
            className={CHECKBOX_CLASSES}
          />
          Show all modified fields
        </label>
        <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-cursor-pointer">
          <input
            type="checkbox"
            data-testid="fieldset-mappings-form-show-all-none-mapped"
            checked={filterMappings === "false"}
            onChange={(): void => setFilterMappings("false")}
            className={CHECKBOX_CLASSES}
          />
          Hide all modified fields
        </label>
        <Button
          data-testid="fieldset-mappings-form-reset-filters"
          variant="secondary-alt"
          compact
          size="sm"
          disabled={!(search ?? filterMappings)}
          onClick={resetFilters}
        >
          Reset filters
        </Button>
      </div>
      <hr className="tw-border-gray-200" />
      <ul
        className="tw-flex tw-flex-col tw-divide-y tw-divide-gray-100 tw-overflow-auto tw-pr-1"
        style={{
          maxHeight: "calc(100vh - 280px)",
          scrollbarGutter: "stable",
        }}
      >
        {filteredKnownKeys.length === 0 && (
          <li
            className="tw-px-3 tw-py-3 tw-text-sm tw-text-gray-500"
            data-testid="fieldset-mappings-form-no-result"
          >
            No fields found
          </li>
        )}
        {filteredKnownKeys.map(({ key }) => {
          const isEditing = editingKey === key;
          return (
            <li
              key={key}
              data-testid={`fieldset-mappings-form-list-item-wrapper-${key}`}
              className={`tw-py-2 tw-px-1 ${
                isEditing ? "tw-bg-gray-100 tw-rounded" : ""
              }`}
              onClick={(): void => {
                if (!isEditing) handleEditKeyMapping(key);
              }}
            >
              <div className="tw-grid tw-grid-cols-[minmax(200px,500px)_1fr_auto] tw-items-center tw-gap-3">
                <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm">
                  <span>{key}</span>
                  {!!keyMappings[key] && (
                    <span
                      data-testid={`fieldset-mappings-form-list-item-${key}-mapped-value`}
                      data-balloon="Display field"
                      data-balloon-pos="right"
                      className="tw-inline-flex tw-items-center tw-gap-1 tw-text-gray-600"
                    >
                      <Icon name="arrow_forward" className="tw-text-base" />
                      {keyMappings[key]}
                    </span>
                  )}
                </div>
                <form
                  data-testid={`fieldset-mappings-form-list-item-${key}-form`}
                  className="tw-flex tw-items-center tw-gap-2"
                  style={{ visibility: isEditing ? "visible" : "hidden" }}
                  onSubmit={(e): void => {
                    e.preventDefault();
                    const target = e.target as typeof e.target & {
                      displayKey: { value: string };
                    };
                    const displayKey = target.displayKey.value;
                    if (!displayKey) {
                      store.dispatch(
                        setDjangoToastOpen({
                          content: "Please provide Display Field",
                          appearance: NotificationAppearance.ERROR,
                        })
                      );
                      return;
                    }
                    if (keyMappings[key]) {
                      handleUpdateKeyMapping(key, displayKey);
                    } else {
                      handleCreateKeyMapping(key, displayKey);
                    }
                  }}
                  onClick={(e): void => e.stopPropagation()}
                >
                  <input
                    name="displayKey"
                    data-testid={`fieldset-mappings-form-list-item-${key}-set-display-key`}
                    placeholder="name your display field..."
                    defaultValue={keyMappings[key] || ""}
                    className="tw-form-input tw-flex-1 tw-rounded-md tw-border-0 tw-p-2 tw-text-sm tw-shadow-sm tw-ring-1 tw-ring-inset tw-ring-gray-300 focus:tw-ring-2 focus:tw-ring-inset focus:tw-ring-gray-400"
                  />
                  <Button
                    type="submit"
                    data-testid={`fieldset-mappings-form-list-item-${key}-action-button`}
                    variant="secondary"
                    size="sm"
                    compact
                  >
                    {keyMappings[key] ? "Update" : "Create"}
                  </Button>
                  {keyMappings[key] && (
                    <Button
                      data-testid={`fieldset-mappings-form-list-item-${key}-delete-button`}
                      variant="secondary-alt"
                      size="sm"
                      compact
                      onClick={(e): Promise<void> => {
                        e.preventDefault();
                        return handleDeleteKeyMapping(key);
                      }}
                    >
                      Delete
                    </Button>
                  )}
                </form>
                <span
                  data-balloon={
                    isEditing ? "Close edit" : `Edit name for field: ${key}`
                  }
                  data-balloon-pos="left"
                >
                  <Button
                    data-testid={`fieldset-mappings-form-list-item-${key}-edit`}
                    variant="secondary-alt"
                    size="sm"
                    icon
                    compact
                    onClick={(e): void => {
                      e.stopPropagation();
                      handleEditKeyMapping(key);
                    }}
                  >
                    <Icon name={isEditing ? "close" : "edit"} />
                  </Button>
                </span>
              </div>
            </li>
          );
        })}
      </ul>
      {loading && (
        <div data-testid="fieldset-mappings-form-loading">
          <PageDimmer header="" loading />
        </div>
      )}
    </div>
  );
};
